import React, { useState, useEffect } from 'react'
import s from '../../styles.module.css'

interface SurveyWeightPickerProps {
  value?: number
  onChange?: (weight: number) => void
  onBlur?: (weight: number) => void
  placeholder?: string
}

const SurveyWeightPicker: React.FC<SurveyWeightPickerProps> = ({
  value: initialValue,
  onChange: propOnChange,
  onBlur: propOnBlur,
  placeholder = 'Weight',
}) => {
  const [stringValue, setStringValue] = useState(
    initialValue ? String(initialValue) : ''
  )
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (initialValue !== undefined && initialValue !== null) {
      setStringValue(String(initialValue))
    } else {
      setStringValue('')
    }
  }, [initialValue])

  const validateWeight = (value: string): string | null => {
    if (!value || value.trim() === '') {
      return null // Empty is allowed (will be handled by required validation)
    }
    const numValue = parseInt(value, 10)
    if (isNaN(numValue) || numValue <= 0) {
      return 'Please enter a valid positive number'
    }
    if (numValue > 1000) {
      return 'Please enter a weight less than 1000 lbs'
    }
    return null
  }

  const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = ev.target.value.replace(/[^0-9]/g, '') // Only allow digits
    setStringValue(newValue)

    const validationError = validateWeight(newValue)
    setError(validationError)

    if (!validationError && newValue) {
      const numValue = parseInt(newValue, 10)
      if (propOnChange) {
        propOnChange(numValue)
      }
    } else if (propOnChange) {
      propOnChange(0)
    }
  }

  const handleBlur = () => {
    const validationError = validateWeight(stringValue)
    setError(validationError)

    if (!validationError && stringValue) {
      const numValue = parseInt(stringValue, 10)
      if (propOnBlur) {
        propOnBlur(numValue)
      }
    } else if (propOnBlur) {
      propOnBlur(0)
    }
  }

  return (
    <div className={s.weightPickerContainer}>
      <div className={s.weightPickerInputWrapper}>
        <input
          className={s.weightPickerInput}
          type="text"
          inputMode="numeric"
          value={stringValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          maxLength={4}
        />
        <label className={s.weightPickerLabel}>lbs</label>
      </div>
      {error && <div className={s.weightPickerError}>{error}</div>}
    </div>
  )
}

export default SurveyWeightPicker
